"use strict";

exports.__esModule = true;
exports.MIN_USERNAME_LENGTH = exports.MAX_USERNAME_LENGTH = exports.MAX_PERSON_NAME_LENGTH = exports.MAX_ENTITY_NAME_LENGTH = exports.DENIED_NAME_CHARACTERS = void 0;
exports.findDeniedCharacters = findDeniedCharacters;
exports.hasControlCharacters = hasControlCharacters;
exports.isUnmodified = isUnmodified;
exports.translateValidationError = translateValidationError;
exports.validateEntityName = validateEntityName;
exports.validateNameCharacters = validateNameCharacters;
exports.validatePersonName = validatePersonName;
exports.validateUsername = validateUsername;
/**
 * Shared input validation rules (VAPT-02).
 *
 * See docs/vapt-remediation/Input-Validation.md for the agreed policy and the
 * reasoning behind it. In short:
 *
 * - Names and identifiers use a permissive denylist: block the characters that
 *   enable injection, allow everything else so that legitimate names such as
 *   O'Brien, Jean-Luc, Garcia and non-Latin scripts still work.
 * - Free text (comments, descriptions) is deliberately NOT character-filtered.
 *   Engineers legitimately write "a < b" or paste snippets. Those fields are
 *   protected by output encoding instead.
 *
 * This layer is defence in depth, not the primary control. The primary controls
 * remain React's output escaping when rendering and correct encoding on export.
 * A denylist fails open against characters nobody anticipated, which is
 * acceptable here only because it is not the thing standing between an attacker
 * and execution. The authoritative check belongs at the API.
 *
 * Validators return an error descriptor rather than a message, so that callers
 * keep control of translation:
 *
 *   const error = validatePersonName(value);
 *   return error ? translate(error.id, error.defaultMessage, error.values) : undefined;
 */

/** Characters denied in names and identifiers. */
var DENIED_NAME_CHARACTERS = exports.DENIED_NAME_CHARACTERS = ["<", ">", "$", "{", "}", "\\", "`"];
var DENIED_NAME_PATTERN = /[<>${}\\`]/g;

// Control characters are never legitimate in a name, and embedded tabs or
// carriage returns are a standard way to slip past naive filters. Built from an
// escaped string so no literal control bytes appear in this source file.
var CONTROL_CHARACTER_PATTERN = new RegExp("[\\u0000-\\u001F\\u007F]");

// Usernames are system identifiers rather than display names, so they get a
// strict allowlist instead. `@` is included because usernames are commonly the
// user's email address.
var USERNAME_PATTERN = /^[a-zA-Z0-9._@-]+$/;
var MAX_PERSON_NAME_LENGTH = exports.MAX_PERSON_NAME_LENGTH = 64;
var MIN_USERNAME_LENGTH = exports.MIN_USERNAME_LENGTH = 3;
var MAX_USERNAME_LENGTH = exports.MAX_USERNAME_LENGTH = 64;
var MAX_ENTITY_NAME_LENGTH = exports.MAX_ENTITY_NAME_LENGTH = 128;

/**
 * Returns the distinct denied characters present in a value, for use in an
 * error message. Empty array when the value is clean or is not a string.
 */
function findDeniedCharacters(value) {
  if (typeof value !== "string") {
    return [];
  }
  var matches = value.match(DENIED_NAME_PATTERN) || [];
  return Array.from(new Set(matches));
}
function hasControlCharacters(value) {
  return typeof value === "string" && CONTROL_CHARACTER_PATTERN.test(value);
}

/**
 * Character rules only, with no opinion on length.
 *
 * Use this where a field already has its own length cap that should be kept --
 * dashboard and report names cap at 35, which is tighter than the shared
 * constants and must not be relaxed by adopting these rules.
 *
 * @returns {{id: string, defaultMessage: string, values: object}|undefined}
 */
function validateNameCharacters(value) {
  if (typeof value !== "string" || !value) {
    return undefined;
  }
  var denied = findDeniedCharacters(value);
  if (denied.length) {
    return {
      id: "Validation.name.deniedCharacters",
      defaultMessage: "Cannot contain {characters}",
      values: {
        characters: denied.join(" ")
      }
    };
  }
  if (hasControlCharacters(value)) {
    return {
      id: "Validation.name.controlCharacters",
      defaultMessage: "Cannot contain control characters",
      values: {}
    };
  }
  return undefined;
}

/**
 * Character rules plus a length cap. Does not check for presence -- callers
 * already have their own field-specific "is required" messages.
 *
 * @returns {{id: string, defaultMessage: string, values: object}|undefined}
 */
function validatePersonName(value, maxLength) {
  if (maxLength === void 0) {
    maxLength = MAX_PERSON_NAME_LENGTH;
  }
  var characterError = validateNameCharacters(value);
  if (characterError) {
    return characterError;
  }
  if (typeof value !== "string" || !value) {
    return undefined;
  }
  if (value.trim().length > maxLength) {
    return {
      id: "Validation.name.tooLong",
      defaultMessage: "Cannot be longer than {maxLength} characters",
      values: {
        maxLength: maxLength
      }
    };
  }
  return undefined;
}

/** Same rules as a person name, with the wider cap used for entity names. */
function validateEntityName(value) {
  return validatePersonName(value, MAX_ENTITY_NAME_LENGTH);
}

/**
 * Validates a username against the strict identifier allowlist.
 *
 * @returns {{id: string, defaultMessage: string, values: object}|undefined}
 */
function validateUsername(value) {
  if (typeof value !== "string" || !value) {
    return undefined;
  }
  var trimmed = value.trim();
  if (trimmed.length < MIN_USERNAME_LENGTH) {
    return {
      id: "Validation.username.tooShort",
      defaultMessage: "Must be at least {minLength} characters",
      values: {
        minLength: MIN_USERNAME_LENGTH
      }
    };
  }
  if (trimmed.length > MAX_USERNAME_LENGTH) {
    return {
      id: "Validation.username.tooLong",
      defaultMessage: "Cannot be longer than {maxLength} characters",
      values: {
        maxLength: MAX_USERNAME_LENGTH
      }
    };
  }
  if (!USERNAME_PATTERN.test(trimmed)) {
    return {
      id: "Validation.username.invalidCharacters",
      defaultMessage: "Can only contain letters, numbers, and the characters . _ - @",
      values: {}
    };
  }
  return undefined;
}

/**
 * True when a field still holds the value it was loaded with -- i.e. the user
 * has not entered or changed anything in it.
 *
 * Format rules describe what NEW input may contain. Applying them to stored
 * values means a record that predates a rule can never be saved: final-form
 * marks the whole form invalid, which disables Save for every field, including
 * when the offending field is read-only and so cannot be corrected. That is
 * what stopped accounts whose username contains a space from being disabled.
 *
 * final-form only supplies `meta` when a validator declares exactly three
 * parameters (see runFieldLevelValidation), so a validator using this MUST be
 * written as `(value, allValues, meta)`. If `meta` is absent the rule still
 * runs, which matches the previous behaviour.
 */
function isUnmodified(meta) {
  return Boolean(meta && meta.dirty === false);
}

/**
 * Resolves an error descriptor to a translated message.
 * Returns undefined when there is no error, which is what react-final-form
 * expects from a field validator.
 */
function translateValidationError(error, translate) {
  if (!error) {
    return undefined;
  }
  return translate(error.id, error.defaultMessage, error.values);
}